import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  YouTube as YouTubeIcon,
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiService, {
  type AdminChannelProgramContentRow,
  type AdminChannelProgramRow,
} from "../services/api";
import { formatChileanDate } from "../utils/dateUtils";
import { slugify } from "../utils/textUtils";

const splitCsv = (value: string): string[] =>
  value
    .split(/[,;\n]+/)
    .map((part) => part.trim())
    .filter(Boolean);

const joinCsv = (items: string[] | undefined): string => (items ?? []).join(", ");

const sortProgramsByName = <T extends { name: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => a.name.localeCompare(b.name, "es", { sensitivity: "base" }));

const sortByPublishedAtDesc = <T extends { publishedAt?: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => {
    const dateA = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
    const dateB = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
    return dateB - dateA;
  });

const getYoutubeContentUrl = (item: AdminChannelProgramContentRow): string | null => {
  if (item.source !== "youtube") return null;
  if (item.url?.trim()) return item.url.trim();
  if (item.sourceId?.trim()) {
    return `https://www.youtube.com/watch?v=${item.sourceId.trim()}`;
  }
  return null;
};

type ProgramFormState = {
  name: string;
  slug: string;
  aliases: string;
  keywords: string;
  cues: string;
  hashtags: string;
  youtubePlaylistIds: string;
  isActive: boolean;
  hidden: boolean;
  isDigital: boolean;
};

const emptyForm = (): ProgramFormState => ({
  name: "",
  slug: "",
  aliases: "",
  keywords: "",
  cues: "",
  hashtags: "",
  youtubePlaylistIds: "",
  isActive: true,
  hidden: false,
  isDigital: false,
});

type ReviewProgramsPanelProps = {
  podcastId?: string;
};

const ReviewProgramsPanel: React.FC<ReviewProgramsPanelProps> = ({
  podcastId,
}) => {
  const queryClient = useQueryClient();

  const [listOpen, setListOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm);
  const [createSlugManual, setCreateSlugManual] = useState(false);

  const [editTarget, setEditTarget] = useState<AdminChannelProgramRow | null>(
    null,
  );
  const [editForm, setEditForm] = useState(emptyForm);
  const [editSlugManual, setEditSlugManual] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<AdminChannelProgramRow | null>(
    null,
  );
  const [deleteMigrateToProgramId, setDeleteMigrateToProgramId] = useState("");
  const [savingContentId, setSavingContentId] = useState<string | null>(null);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const programsQuery = useQuery({
    queryKey: ["admin-channel-programs", podcastId, "all"],
    queryFn: () =>
      apiService.getAdminChannelPrograms(podcastId!, { allContent: true }),
    enabled: Boolean(podcastId),
    staleTime: 30_000,
  });

  const podcastQuery = useQuery({
    queryKey: ["podcast", podcastId],
    queryFn: () => apiService.getPodcast(podcastId!),
    enabled: Boolean(podcastId),
  });
  const canMarkDigital =
    podcastQuery.data?.type === "tv" || podcastQuery.data?.category === "politics";

  const programs = programsQuery.data?.programs ?? [];
  const catalogPrograms = programsQuery.data?.catalogPrograms ?? [];
  const programsSorted = useMemo(() => sortProgramsByName(programs), [programs]);

  const editProgramContentQuery = useQuery({
    queryKey: ["admin-channel-program-content", podcastId, editTarget?._id, "all"],
    queryFn: () =>
      apiService.getAdminChannelProgramContent(podcastId!, editTarget!._id, {
        allContent: true,
      }),
    enabled: Boolean(podcastId && editTarget?._id),
  });

  const editProgramContentSorted = useMemo(
    () => sortByPublishedAtDesc(editProgramContentQuery.data?.content ?? []),
    [editProgramContentQuery.data?.content],
  );

  const reassignTargets = useMemo(
    () =>
      sortProgramsByName(
        catalogPrograms.filter((p) => p._id !== editTarget?._id && p.isActive !== false),
      ),
    [catalogPrograms, editTarget?._id],
  );

  useEffect(() => {
    if (!editTarget) return;
    setEditSlugManual(Boolean(editTarget.slug));
    setEditForm({
      name: editTarget.name ?? "",
      slug: editTarget.slug ?? "",
      aliases: joinCsv(editTarget.aliases),
      keywords: joinCsv(editTarget.keywords),
      cues: joinCsv(editTarget.cues),
      hashtags: joinCsv(editTarget.hashtags),
      youtubePlaylistIds: joinCsv(editTarget.youtubePlaylistIds),
      isActive: editTarget.isActive !== false,
      hidden: editTarget.hidden === true,
      isDigital: editTarget.isDigital === true,
    });
  }, [editTarget]);

  useEffect(() => {
    if (!deleteTarget) setDeleteMigrateToProgramId("");
  }, [deleteTarget]);

  const invalidateProgramQueries = () => {
    queryClient.invalidateQueries({
      queryKey: ["admin-channel-programs", podcastId],
    });
    queryClient.invalidateQueries({ queryKey: ["podcast-programs", podcastId] });
    queryClient.invalidateQueries({ queryKey: ["podcast-content-analysis"] });
  };

  const createMutation = useMutation({
    mutationFn: () => {
      const name = createForm.name.trim();
      if (!name) throw new Error("Program name is required");
      return apiService.createAdminChannelProgram(podcastId!, {
        name,
        slug: createForm.slug.trim() || undefined,
        aliases: splitCsv(createForm.aliases),
        keywords: splitCsv(createForm.keywords),
        cues: splitCsv(createForm.cues),
        hashtags: splitCsv(createForm.hashtags),
        youtubePlaylistIds: splitCsv(createForm.youtubePlaylistIds),
        isActive: createForm.isActive,
        hidden: createForm.hidden,
        isDigital: canMarkDigital ? createForm.isDigital : undefined,
      });
    },
    onSuccess: (result) => {
      invalidateProgramQueries();
      setCreateOpen(false);
      setCreateForm(emptyForm());
      setCreateSlugManual(false);
      setSnackbar({
        open: true,
        message: `Program "${result.program.name}" created`,
        severity: "success",
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: "Could not create program",
        severity: "error",
      });
    },
  });

  const editMutation = useMutation({
    mutationFn: () => {
      const name = editForm.name.trim();
      if (!name) throw new Error("Program name is required");
      return apiService.patchAdminChannelProgram(podcastId!, editTarget!._id, {
        name,
        slug: editForm.slug.trim() || undefined,
        aliases: splitCsv(editForm.aliases),
        keywords: splitCsv(editForm.keywords),
        cues: splitCsv(editForm.cues),
        hashtags: splitCsv(editForm.hashtags),
        youtubePlaylistIds: splitCsv(editForm.youtubePlaylistIds),
        isActive: editForm.isActive,
        hidden: editForm.hidden,
        isDigital: canMarkDigital ? editForm.isDigital : undefined,
      });
    },
    onSuccess: () => {
      invalidateProgramQueries();
      setEditTarget(null);
      setSnackbar({
        open: true,
        message: "Program updated",
        severity: "success",
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: "Could not update program",
        severity: "error",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (payload: {
      programId: string;
      migrateToProgramId?: string;
      unlinkContent?: boolean;
    }) =>
      apiService.deleteAdminChannelProgram(podcastId!, payload.programId, {
        migrateToProgramId: payload.migrateToProgramId,
        unlinkContent: payload.unlinkContent,
      }),
    onSuccess: (result) => {
      invalidateProgramQueries();
      setDeleteTarget(null);
      setDeleteMigrateToProgramId("");
      let detail = "";
      if (result.migratedContentCount > 0) {
        detail = ` (${result.migratedContentCount} episode(s) migrated)`;
      } else if (result.unlinkedContentCount > 0) {
        detail = ` (${result.unlinkedContentCount} episode(s) unlinked)`;
      }
      setSnackbar({
        open: true,
        message: `Program deleted${detail}`,
        severity: "success",
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: "Could not delete program",
        severity: "error",
      });
    },
  });

  const reassignContentMutation = useMutation({
    mutationFn: ({
      contentId,
      programId,
    }: {
      contentId: string;
      programId: string | null;
    }) =>
      apiService.patchAdminChannelContentProgram(podcastId!, contentId, programId),
    onMutate: ({ contentId }) => {
      setSavingContentId(contentId);
    },
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-channel-program-content", podcastId, editTarget?._id],
      });
      invalidateProgramQueries();
      const targetName =
        variables.programId == null
          ? "Unassigned"
          : catalogPrograms.find((p) => p._id === variables.programId)?.name ??
            "another program";
      setSnackbar({
        open: true,
        message: `Episode moved to ${targetName}`,
        severity: "success",
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: "Could not reassign episode",
        severity: "error",
      });
    },
    onSettled: () => {
      setSavingContentId(null);
    },
  });

  const deleteMigrationTargets = useMemo(
    () =>
      sortProgramsByName(
        catalogPrograms.filter((p) => p._id !== deleteTarget?._id),
      ),
    [catalogPrograms, deleteTarget?._id],
  );

  const deleteTargetHasContent = (deleteTarget?.contentCount ?? 0) > 0;
  const canDeleteWithMigration =
    Boolean(deleteTarget) &&
    deleteTargetHasContent &&
    deleteMigrationTargets.length > 0 &&
    Boolean(deleteMigrateToProgramId);
  const canDeleteWithUnlink =
    Boolean(deleteTarget) &&
    deleteTargetHasContent &&
    deleteMigrationTargets.length === 0;
  const canDeleteEmpty = Boolean(deleteTarget) && !deleteTargetHasContent;

  const openCreateDialog = () => {
    setCreateForm(emptyForm());
    setCreateSlugManual(false);
    setCreateOpen(true);
  };

  const handleCreateNameChange = (name: string) => {
    setCreateForm((f) => ({
      ...f,
      name,
      slug: createSlugManual ? f.slug : slugify(name),
    }));
  };

  const handleEditNameChange = (name: string) => {
    setEditForm((f) => ({
      ...f,
      name,
      slug: editSlugManual ? f.slug : slugify(name),
    }));
  };

  const previewList = (items: string[] | undefined, max = 3): string => {
    const list = items ?? [];
    if (list.length === 0) return "—";
    if (list.length <= max) return list.join(", ");
    return `${list.slice(0, max).join(", ")} +${list.length - max}`;
  };

  const renderFormFields = (
    form: ProgramFormState,
    setForm: React.Dispatch<React.SetStateAction<ProgramFormState>>,
    opts: {
      onNameChange: (name: string) => void;
      onSlugChange: (slug: string) => void;
    },
  ) => (
    <Stack spacing={2} sx={{ pt: 0.5 }}>
      <TextField
        label="Name"
        value={form.name}
        onChange={(e) => opts.onNameChange(e.target.value)}
        fullWidth
        required
        size="small"
      />
      <TextField
        label="Slug"
        value={form.slug}
        onChange={(e) => opts.onSlugChange(e.target.value)}
        fullWidth
        size="small"
        helperText="Auto-generated from the name; you can edit it."
      />
      <TextField
        label="Aliases (comma-separated)"
        value={form.aliases}
        onChange={(e) => setForm((f) => ({ ...f, aliases: e.target.value }))}
        fullWidth
        size="small"
        helperText="Alternate names used in program classification"
      />
      <TextField
        label="Keywords (comma-separated)"
        value={form.keywords}
        onChange={(e) => setForm((f) => ({ ...f, keywords: e.target.value }))}
        fullWidth
        size="small"
        helperText="Matched in episode titles"
      />
      <TextField
        label="Cues (comma-separated)"
        value={form.cues}
        onChange={(e) => setForm((f) => ({ ...f, cues: e.target.value }))}
        fullWidth
        size="small"
        helperText="Matched in episode description (e.g. clip extraido)"
      />
      <TextField
        label="Hashtags without # (comma-separated)"
        value={form.hashtags}
        onChange={(e) => setForm((f) => ({ ...f, hashtags: e.target.value }))}
        fullWidth
        size="small"
      />
      <TextField
        label="YouTube playlist IDs (comma-separated)"
        value={form.youtubePlaylistIds}
        onChange={(e) =>
          setForm((f) => ({ ...f, youtubePlaylistIds: e.target.value }))
        }
        fullWidth
        size="small"
      />
      <FormControlLabel
        control={
          <Switch
            checked={form.isActive}
            onChange={(e) =>
              setForm((f) => ({ ...f, isActive: e.target.checked }))
            }
          />
        }
        label="Active"
      />
      <FormControlLabel
        control={
          <Switch
            checked={form.hidden}
            onChange={(e) =>
              setForm((f) => ({ ...f, hidden: e.target.checked }))
            }
          />
        }
        label="Hidden (excluded from rankings and explorer)"
      />
      {canMarkDigital && (
        <FormControlLabel
          control={
            <Switch
              checked={form.isDigital}
              onChange={(e) =>
                setForm((f) => ({ ...f, isDigital: e.target.checked }))
              }
            />
          }
          label="100% digital"
        />
      )}
    </Stack>
  );

  if (!podcastId) return null;

  const canCloseList =
    !createOpen &&
    !editTarget &&
    !deleteTarget &&
    !createMutation.isPending &&
    !editMutation.isPending &&
    !deleteMutation.isPending &&
    !reassignContentMutation.isPending;

  return (
    <>
      <Button size="small" variant="outlined" onClick={() => setListOpen(true)}>
        Ver programas
        {programsSorted.length > 0 ? ` (${programsSorted.length})` : ""}
      </Button>

      <Dialog
        open={listOpen}
        onClose={() => canCloseList && setListOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
          >
            <Typography variant="h6" component="span">
              Programs
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={openCreateDialog}
              disabled={createMutation.isPending}
            >
              Add program
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {programsQuery.isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : programsQuery.isError ? (
            <Typography variant="body2" color="error">
              Could not load programs. Admin access is required.
            </Typography>
          ) : programsSorted.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No programs for this podcast yet. Add one to start classifying
              episodes.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Program</TableCell>
                    <TableCell>Aliases / keywords / cues</TableCell>
                    <TableCell align="right">Episodes</TableCell>
                    <TableCell align="right" width={96} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {programsSorted.map((prog) => (
                    <TableRow key={prog._id}>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2">{prog.name}</Typography>
                          <Stack direction="row" gap={0.5} flexWrap="wrap">
                            {!prog.isActive && (
                              <Chip size="small" label="inactive" />
                            )}
                            {prog.hidden && (
                              <Chip size="small" label="hidden" variant="outlined" />
                            )}
                            {prog.isDigital && (
                              <Chip
                                size="small"
                                label="digital"
                                color="info"
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          A: {previewList(prog.aliases)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          K: {previewList(prog.keywords)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          C: {previewList(prog.cues)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{prog.contentCount}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit program">
                          <span>
                            <IconButton
                              size="small"
                              disabled={
                                editMutation.isPending || deleteMutation.isPending
                              }
                              onClick={() => setEditTarget(prog)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete program">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={
                                editMutation.isPending || deleteMutation.isPending
                              }
                              onClick={() => setDeleteTarget(prog)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setListOpen(false)} disabled={!canCloseList}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createOpen}
        onClose={() => !createMutation.isPending && setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add program</DialogTitle>
        <DialogContent dividers>
          {renderFormFields(createForm, setCreateForm, {
            onNameChange: handleCreateNameChange,
            onSlugChange: (slug) => {
              setCreateSlugManual(true);
              setCreateForm((f) => ({ ...f, slug }));
            },
          })}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateOpen(false)}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending || !createForm.name.trim()}
          >
            {createMutation.isPending ? "Creating…" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editTarget)}
        onClose={() =>
          !editMutation.isPending &&
          !reassignContentMutation.isPending &&
          setEditTarget(null)
        }
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Edit program</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 5 }}>
              {renderFormFields(editForm, setEditForm, {
                onNameChange: handleEditNameChange,
                onSlugChange: (slug) => {
                  setEditSlugManual(true);
                  setEditForm((f) => ({ ...f, slug }));
                },
              })}
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Stack spacing={1.5} sx={{ height: "100%" }}>
                <Typography variant="subtitle2">Assigned episodes</Typography>
                <Typography variant="caption" color="text.secondary">
                  {editProgramContentQuery.isLoading
                    ? "Loading…"
                    : `${editProgramContentSorted.length} episode(s) (video, >5 min). Reassign to fix misclassifications.`}
                </Typography>
                {editProgramContentQuery.isLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : editProgramContentQuery.isError ? (
                  <Typography variant="body2" color="error">
                    Could not load assigned episodes.
                  </Typography>
                ) : editProgramContentSorted.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No episodes assigned to this program.
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      maxHeight: "min(60vh, 520px)",
                      overflow: "auto",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      px: 1.5,
                      py: 1,
                    }}
                  >
                    <Stack divider={<Divider flexItem />} spacing={1.5}>
                      {editProgramContentSorted.map((item) => {
                        const isSaving = savingContentId === item._id;
                        const youtubeUrl = getYoutubeContentUrl(item);
                        return (
                          <Stack key={item._id} spacing={1}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="flex-start"
                            >
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ wordBreak: "break-word" }}
                                >
                                  {item.title}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  {item.publishedAt
                                    ? formatChileanDate(item.publishedAt)
                                    : "No date"}
                                </Typography>
                              </Box>
                              {youtubeUrl ? (
                                <Tooltip title="Open on YouTube">
                                  <IconButton
                                    size="small"
                                    component="a"
                                    href={youtubeUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ mt: -0.25 }}
                                  >
                                    <YouTubeIcon fontSize="small" color="error" />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                            </Stack>
                            <FormControl fullWidth size="small">
                              <InputLabel>Program</InputLabel>
                              <Select
                                value={editTarget?._id ?? ""}
                                label="Program"
                                disabled={isSaving || reassignContentMutation.isPending}
                                onChange={(e) => {
                                  const next = String(e.target.value);
                                  if (!editTarget || next === editTarget._id) return;
                                  reassignContentMutation.mutate({
                                    contentId: item._id,
                                    programId: next || null,
                                  });
                                }}
                              >
                                <MenuItem value={editTarget?._id ?? ""}>
                                  {editTarget?.name}
                                </MenuItem>
                                {reassignTargets.map((prog) => (
                                  <MenuItem key={prog._id} value={prog._id}>
                                    {prog.name}
                                  </MenuItem>
                                ))}
                                <MenuItem value="">
                                  <em>Unassign</em>
                                </MenuItem>
                              </Select>
                            </FormControl>
                            {isSaving ? (
                              <Typography variant="caption" color="text.secondary">
                                Saving…
                              </Typography>
                            ) : null}
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditTarget(null)}
            disabled={editMutation.isPending || reassignContentMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => editMutation.mutate()}
            disabled={
              editMutation.isPending ||
              reassignContentMutation.isPending ||
              !editForm.name.trim()
            }
          >
            {editMutation.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleteMutation.isPending && setDeleteTarget(null)}
      >
        <DialogTitle>Delete program</DialogTitle>
        <DialogContent>
          {deleteTargetHasContent ? (
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <Alert severity="warning">
                <strong>{deleteTarget?.name}</strong> has{" "}
                {deleteTarget?.contentCount} episode(s) assigned.
                {deleteMigrationTargets.length > 0
                  ? " Choose another program to migrate them before deleting."
                  : " There are no other programs; deleting will unlink that content."}
              </Alert>
              {deleteMigrationTargets.length > 0 && (
                <FormControl fullWidth size="small">
                  <InputLabel>Migrate content to</InputLabel>
                  <Select
                    value={deleteMigrateToProgramId}
                    label="Migrate content to"
                    onChange={(e) =>
                      setDeleteMigrateToProgramId(String(e.target.value))
                    }
                  >
                    <MenuItem value="">
                      <em>Select…</em>
                    </MenuItem>
                    {deleteMigrationTargets.map((prog) => (
                      <MenuItem key={prog._id} value={prog._id}>
                        {prog.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Stack>
          ) : (
            <DialogContentText>
              Delete <strong>{deleteTarget?.name}</strong>? This program has no
              assigned content.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </Button>
          {canDeleteWithMigration && (
            <Button
              variant="contained"
              color="error"
              onClick={() =>
                deleteMutation.mutate({
                  programId: deleteTarget!._id,
                  migrateToProgramId: deleteMigrateToProgramId,
                })
              }
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting…" : "Migrate & delete"}
            </Button>
          )}
          {canDeleteWithUnlink && (
            <Button
              variant="contained"
              color="error"
              onClick={() =>
                deleteMutation.mutate({
                  programId: deleteTarget!._id,
                  unlinkContent: true,
                })
              }
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete & unlink"}
            </Button>
          )}
          {canDeleteEmpty && (
            <Button
              variant="contained"
              color="error"
              onClick={() =>
                deleteMutation.mutate({
                  programId: deleteTarget!._id,
                })
              }
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ReviewProgramsPanel;
